import { useState, useEffect, ReactNode, useCallback } from 'react'
import { ArrowUp } from 'lucide-react'
import { LandingNav } from './landing-nav'
import { CookieConsent } from '@/components/landing/CookieConsent'
import { LandingFooter } from './landing-footer'
import { useSmoothScroll } from '@/lib/animations'

interface LandingLayoutProps {
  children: ReactNode
}

export function LandingLayout({ children }: LandingLayoutProps) {
  // Read the saved theme up front: reading it in an effect let the write
  // effect below store 'light' first (StrictMode runs effects twice).
  const [isDark, setIsDark] = useState(() => {
    try {
      const stored = localStorage.getItem('theme')
      if (stored) return stored === 'dark'
      return window.matchMedia('(prefers-color-scheme: dark)').matches
    } catch {
      return false
    }
  })
  const [showScrollTop, setShowScrollTop] = useState(false)

  // Smooth scrolling for in-page anchor links
  useSmoothScroll()

  useEffect(() => {
    if (isDark) {
      document.documentElement.classList.add('dark')
      localStorage.setItem('theme', 'dark')
    } else {
      document.documentElement.classList.remove('dark')
      localStorage.setItem('theme', 'light')
    }
  }, [isDark])

  // Track scroll position for scroll-to-top button
  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 400)
    }
    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  const scrollToTop = useCallback(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }, [])

  return (
    <div className="landing-page min-h-screen bg-white dark:bg-[#0a0a0f] transition-colors duration-200">
      <LandingNav />
      {children}
      <LandingFooter isDark={isDark} onThemeToggle={() => setIsDark(!isDark)} />
      <CookieConsent />

      {/* Scroll to Top Button - Fluent 2 Motion */}
      <button
        onClick={scrollToTop}
        aria-label="Scroll to top"
        className={`
          fixed bottom-8 right-8 z-50
          w-12 h-12 rounded-full
          bg-[#1a1a1a] dark:bg-white
          text-white dark:text-[#1a1a1a]
          shadow-[0_4px_8px_rgba(0,0,0,0.14),0_0_2px_rgba(0,0,0,0.12)]
          dark:shadow-[0_4px_8px_rgba(0,0,0,0.3),0_0_2px_rgba(0,0,0,0.2)]
          hover:shadow-[0_8px_16px_rgba(0,0,0,0.14),0_0_2px_rgba(0,0,0,0.12)]
          dark:hover:shadow-[0_8px_16px_rgba(0,0,0,0.3),0_0_2px_rgba(0,0,0,0.2)]
          hover:scale-105 active:scale-95
          flex items-center justify-center
          transition-all duration-300 ease-[cubic-bezier(0.33,0,0.67,1)]
          ${showScrollTop ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4 pointer-events-none'}
        `}
      >
        <ArrowUp className="h-5 w-5" />
      </button>
    </div>
  )
}

export default LandingLayout
